import React, { useState } from 'react';
import { Droplets, Printer, Download } from 'lucide-react';
import { cashFlowApi, CashFlowFilter, CashFlowReportDto } from '@/api/cashFlow.api';
import { useAuth } from '@/hooks/useAuth';
import { exportToExcel, printReport } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';

const CASH_FLOW_EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'section', header: 'Section', width: 26 },
  { key: 'account', header: 'Account', width: 44 },
  { key: 'amount',  header: 'Amount',  width: 18, numFmt: '#,##0.00', align: 'right' },
];

function buildCashFlowExcelRows(report: CashFlowReportDto): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  const sections: Array<{ title: string; lines: CashFlowReportDto['operatingActivities']; total: number }> = [
    { title: 'Operating Activities', lines: report.operatingActivities, total: report.netOperating },
    { title: 'Investing Activities', lines: report.investingActivities, total: report.netInvesting },
    { title: 'Financing Activities', lines: report.financingActivities, total: report.netFinancing },
  ];
  for (const s of sections) {
    for (const l of s.lines) {
      rows.push({ section: s.title, account: `${l.accountCode} — ${l.accountName}`, amount: l.netMovement });
    }
    rows.push({ section: s.title, account: `Net ${s.title}`, amount: s.total });
  }
  rows.push({ section: 'Summary', account: 'Net Cash Movement',     amount: report.netCashMovement });
  rows.push({ section: 'Summary', account: 'Opening Cash Balance',  amount: report.openingCashBalance });
  rows.push({ section: 'Summary', account: 'Closing Cash Balance',  amount: report.closingCashBalance });
  return rows;
}

export const CashFlowPage: React.FC = () => {
  const { user } = useAuth();
  const companyId = user?.companyId ?? '';

  const [filter, setFilter] = useState<CashFlowFilter>({
    companyId,
    financialYearCode: '',
    dateFrom: '',
    dateTo: '',
    currencyCode: '',
  });
  const [report, setReport]   = useState<CashFlowReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const runReport = async () => {
    if (!filter.financialYearCode) { setError('Financial year is required.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await cashFlowApi.getReport({ ...filter, companyId });
      if (res.data?.success) {
        setReport(res.data.data ?? null);
      } else {
        setError(res.data?.message ?? 'Failed to generate report.');
      }
    } catch {
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!report) return;
    exportToExcel(
      {
        sheetName: 'Cash Flow',
        columns: CASH_FLOW_EXCEL_COLUMNS,
        rows: buildCashFlowExcelRows(report),
        title: 'Cash Flow Statement',
        subtitle: `${report.financialYearCode}${report.dateFrom ? ` | ${report.dateFrom} to ${report.dateTo}` : ''} | ${report.currencyCode}`,
      },
      `cash-flow-${report.financialYearCode}`,
    );
  };

  const handlePrint = () => {
    printReport('Cash Flow Statement');
  };

  const fmt = (n: number) =>
    n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const Section = ({ title, lines, total, color }: {
    title: string;
    lines: CashFlowReportDto['operatingActivities'];
    total: number;
    color: string;
  }) => (
    <div className="mb-6">
      <h3 className={`text-sm font-semibold uppercase tracking-wide mb-2 ${color}`}>{title}</h3>
      <table className="w-full text-sm border-collapse">
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
              <td className="py-1 pl-4 text-gray-700 dark:text-gray-300">{l.accountCode} — {l.accountName}</td>
              <td className="py-1 pr-2 text-right font-mono text-gray-800 dark:text-gray-200">{fmt(l.netMovement)}</td>
            </tr>
          ))}
          {lines.length === 0 && (
            <tr><td colSpan={2} className="py-1 pl-4 text-gray-400 italic">No activity</td></tr>
          )}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-semibold">
            <td className="py-2 pl-4">Net {title}</td>
            <td className={`py-2 pr-2 text-right font-mono ${total >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
              {fmt(total)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <Droplets className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <h1 className="text-xl font-semibold">Cash Flow Statement</h1>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Financial Year *</label>
            <input
              type="text"
              value={filter.financialYearCode}
              onChange={e => setFilter(f => ({ ...f, financialYearCode: e.target.value }))}
              placeholder="e.g. FY2025"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date From</label>
            <input
              type="date"
              value={filter.dateFrom}
              onChange={e => setFilter(f => ({ ...f, dateFrom: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date To</label>
            <input
              type="date"
              value={filter.dateTo}
              onChange={e => setFilter(f => ({ ...f, dateTo: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Currency</label>
            <input
              type="text"
              value={filter.currencyCode}
              onChange={e => setFilter(f => ({ ...f, currencyCode: e.target.value }))}
              placeholder="e.g. USD"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-4 flex gap-2">
          <button
            onClick={runReport}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Generating…' : 'Run Report'}
          </button>
          {report && (
            <>
              <button
                onClick={handlePrint}
                className="flex items-center gap-1 border border-gray-300 dark:border-gray-600 px-3 py-2 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <Printer className="h-4 w-4" /> Print
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-1 border border-gray-300 dark:border-gray-600 px-3 py-2 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <Download className="h-4 w-4" /> Export
              </button>
            </>
          )}
        </div>
      </div>

      {/* Report output */}
      {report && (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <div className="text-center mb-6">
            <h2 className="text-lg font-bold">Cash Flow Statement</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {report.financialYearCode}
              {report.dateFrom && ` | ${report.dateFrom} to ${report.dateTo}`}
              {' | '}{report.currencyCode}
            </p>
          </div>

          <Section
            title="Operating Activities"
            lines={report.operatingActivities}
            total={report.netOperating}
            color="text-blue-700 dark:text-blue-400"
          />
          <Section
            title="Investing Activities"
            lines={report.investingActivities}
            total={report.netInvesting}
            color="text-purple-700 dark:text-purple-400"
          />
          <Section
            title="Financing Activities"
            lines={report.financingActivities}
            total={report.netFinancing}
            color="text-orange-700 dark:text-orange-400"
          />

          {/* Summary */}
          <div className="border-t-2 border-gray-800 pt-4 mt-4">
            <table className="w-full text-sm">
              <tbody>
                <tr className="font-semibold">
                  <td className="py-1">Net Cash Movement</td>
                  <td className={`py-1 text-right font-mono ${report.netCashMovement >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                    {fmt(report.netCashMovement)}
                  </td>
                </tr>
                <tr className="text-gray-600 dark:text-gray-400">
                  <td className="py-1 pl-4">Opening Cash Balance</td>
                  <td className="py-1 text-right font-mono">{fmt(report.openingCashBalance)}</td>
                </tr>
                <tr className="font-bold text-base border-t border-gray-300 dark:border-gray-600">
                  <td className="py-2">Closing Cash Balance</td>
                  <td className={`py-2 text-right font-mono ${report.closingCashBalance >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                    {fmt(report.closingCashBalance)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
